import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, session } from "@/lib/api";
import { Users, Save, Lock, AlertTriangle, Layers, Info, Wand2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Inline teams-per-player editor — used in both AdminPanel → Settings and
 * the DraftRoom pre-draft setup screen.
 *
 * Editable ONLY when no draft exists yet for THIS tournament (per
 * `league.drafts_by_tournament[tournament.id]`).
 *
 * Live preview mirrors the backend's two-mode routing:
 *   • STRICT  → players ≤ ⌊teams / TPP⌋. Each player gets TPP UNIQUE teams.
 *   • OVERLAP → players  > ⌊teams / TPP⌋. Each player gets one unique
 *               C(teams, TPP) portfolio — teams overlap across rosters.
 *
 * iter49b additions:
 *   • Info `(i)` popover next to the header explaining the two modes.
 *   • "Auto-fit" pill — sets TPP to the LARGEST value that still fits the
 *     current player count (in either mode). Hides when the current value
 *     is already optimal.
 */
export default function TeamsPerPlayerCard({
    league, participants, tournament, onChanged, compact = false,
}) {
    const currentValue = (tournament?.teams_per_participant
        ?? league?.teams_per_participant
        ?? 2);
    const [draft, setDraft] = useState(currentValue);
    const [saving, setSaving] = useState(false);
    const [capacity, setCapacity] = useState(null);

    useEffect(() => { setDraft(currentValue); }, [currentValue]);

    useEffect(() => {
        if (!tournament?.id) return;
        let cancelled = false;
        api.getDraftCapacity(tournament.id).then(c => {
            if (!cancelled) setCapacity(c);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [tournament?.id, currentValue]);

    // iter52c — Whether THIS tournament has a draft. Trust the per-tournament
    // dict only — the iter52 migration backfilled every existing draft. The
    // legacy `league.tournament_id` is the league's PRIMARY pointer (not the
    // tournament that owns the current draft), so checking it would falsely
    // flag the primary tournament as locked whenever any tournament in the
    // league has a draft.
    const tid = tournament?.id;
    const hasDraftForThisTournament = !!(tid && league?.drafts_by_tournament?.[tid]);
    const playerCount = capacity?.players ?? (participants || []).filter(p => p.is_player !== false).length;
    const teamCount = capacity?.team_count ?? (tournament?.teams?.length || 0);

    const effective = capacity?.slots ?? draft;
    const willClamp = capacity?.clamped ?? false;
    const mode = capacity?.mode ?? "strict";
    const fits = capacity?.fits ?? true;
    const maxFit = capacity?.max_fitting_slots ?? 0;
    // Show the Auto-fit pill only when there's actually a better/different
    // value available than the currently-saved TPP.
    const showAutoFit = !hasDraftForThisTournament && maxFit > 0 && maxFit !== currentValue && playerCount > 0;

    const dirty = draft !== currentValue;

    const save = async (valueOverride) => {
        const valueToSave = valueOverride ?? draft;
        if (valueToSave === currentValue) return;
        setSaving(true);
        try {
            if (tournament?.id) {
                const sess = session.load?.();
                await api.setTournamentTeamsPerParticipant(
                    tournament.id, valueToSave, sess?.participant_id,
                );
            } else {
                await api.setTeamsPerParticipant(league.id, valueToSave);
            }
            toast.success(`Teams per crewmate set to ${valueToSave}`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
            setDraft(currentValue);
        }
        setSaving(false);
    };

    const applyAutoFit = () => {
        if (!maxFit || saving) return;
        setDraft(maxFit);
        save(maxFit);
    };

    return (
        <div className={`brutal-card p-5 space-y-3 ${compact ? "" : ""}`} data-testid="teams-per-player-card">
            <div className="flex items-center gap-2 flex-wrap">
                <Users className="w-5 h-5 text-cyber" />
                <h2 className="font-display font-extrabold text-xl uppercase">Teams per crewmate</h2>
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            data-testid="teams-per-player-info-btn"
                            aria-label="What is Teams Per Crewmate?"
                            className="w-6 h-6 rounded-full border border-zinc-600 text-zinc-400 hover:text-cyber hover:border-cyber transition-colors flex items-center justify-center"
                        >
                            <Info className="w-3.5 h-3.5" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="start"
                        sideOffset={8}
                        data-testid="teams-per-player-info-popover"
                        className="w-80 bg-ink border-2 border-cyber/40 text-zinc-200 shadow-brutal space-y-3"
                    >
                        <p className="font-display font-extrabold text-sm uppercase tracking-wide text-cyber">How this works</p>
                        <div className="space-y-2 text-xs leading-relaxed">
                            <p>
                                <span className="font-bold text-volt">Strict mode</span> — when you have enough teams for everyone to get a unique set
                                (<span className="font-mono text-cyber">players ≤ ⌊teams ÷ TPP⌋</span>). Snake-by-tier draft, no team overlap.
                            </p>
                            <p>
                                <span className="font-bold text-cyber">Portfolio mode</span> — when there aren't enough teams to go around
                                (<span className="font-mono text-cyber">players &gt; ⌊teams ÷ TPP⌋</span>). The draft enumerates every unique combination of
                                <span className="font-mono"> {Math.max(2, effective || 2)} </span>teams and deals one to each crewmate. Teams overlap, but every roster is still unique.
                            </p>
                            <p className="text-zinc-400 italic">
                                The draft picks the right mode automatically based on crewmate count. You just set TPP.
                            </p>
                            {showAutoFit && (
                                <p className="text-zinc-400">
                                    The <span className="text-blaze font-bold">Auto-fit</span> pill suggests the largest TPP that keeps the draft in <span className="text-volt">strict mode</span> (every crewmate gets unique teams). When crewmates outnumber teams, it falls back to the largest portfolio-mode TPP that fits.
                                </p>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
                {hasDraftForThisTournament && (
                    <span className="pill bg-ink border-blaze/40 text-blaze text-[10px] font-bold flex items-center gap-1" data-testid="teams-per-player-locked-pill">
                        <Lock className="w-3 h-3" /> LOCKED
                    </span>
                )}
            </div>

            {hasDraftForThisTournament ? (
                <p className="text-xs text-zinc-400 leading-relaxed" data-testid="teams-per-player-locked-note">
                    Clear the current draft to adjust this. Once cleared, you'll be able to pick a new value before the next draft kicks off.
                </p>
            ) : (
                <p className="text-xs text-zinc-400 leading-relaxed">
                    Set how many teams each crewmate drafts. Up to 10 — when there aren't enough teams for every crewmate to get a unique set, the draft switches to <strong className="text-cyber">portfolio mode</strong> and crewmates will share teams (every roster is still a unique combination).
                </p>
            )}

            <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <select
                    data-testid="teams-per-player-select"
                    value={draft}
                    onChange={(e) => setDraft(Number(e.target.value))}
                    disabled={hasDraftForThisTournament || saving}
                    className="brutal-input w-full appearance-none cursor-pointer pr-8 bg-no-repeat bg-[length:14px] bg-[position:right_12px_center] bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20fill=%22none%22%20stroke=%22%2300e5ff%22%20stroke-width=%223%22%20viewBox=%220%200%2024%2024%22><polyline%20points=%226%209%2012%2015%2018%209%22/></svg>')] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                        <option key={n} value={n}>{`${n} ${n === 1 ? "team" : "teams"} per crewmate`}</option>
                    ))}
                </select>
                <button
                    data-testid="teams-per-player-save-btn"
                    onClick={() => save()}
                    disabled={!dirty || hasDraftForThisTournament || saving}
                    className="brutal-btn-primary disabled:opacity-30 text-sm py-2"
                >
                    <Save className="w-4 h-4" /> Save
                </button>
            </div>

            {/* Auto-fit pill — suggests the largest TPP that fits current players */}
            {showAutoFit && (
                <button
                    type="button"
                    data-testid="teams-per-player-auto-fit-btn"
                    onClick={applyAutoFit}
                    disabled={saving}
                    className="pill bg-blaze/15 border border-blaze text-blaze hover:bg-blaze/25 transition-colors text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-50"
                    title={`Set TPP to ${maxFit} — the largest value that still fits ${playerCount} crewmate${playerCount === 1 ? "" : "s"}.`}
                >
                    <Wand2 className="w-3 h-3" />
                    Auto-fit: use {maxFit} team{maxFit === 1 ? "" : "s"} per crewmate
                </button>
            )}

            {/* Live preview — surfaces mode + clamping + fit error */}
            {!hasDraftForThisTournament && capacity && (
                <div className="space-y-2" data-testid="teams-per-player-preview">
                    <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
                        <span>{playerCount} crewmate{playerCount === 1 ? "" : "s"} · {teamCount} teams</span>
                        {mode === "overlap" ? (
                            <span className="pill bg-cyber/15 border-cyber/40 text-cyber text-[10px] font-bold flex items-center gap-1" data-testid="teams-per-player-mode-pill">
                                <Layers className="w-3 h-3" /> portfolio mode
                            </span>
                        ) : (
                            <span className="pill bg-volt/15 border-volt/40 text-volt text-[10px] font-bold" data-testid="teams-per-player-mode-pill">
                                {willClamp ? "strict mode only" : "strict mode"}
                            </span>
                        )}
                        {willClamp && fits && (
                            <span className="pill bg-blaze/15 border-blaze text-blaze text-[10px] font-bold flex items-center gap-1" data-testid="teams-per-player-clamp-warning">
                                <AlertTriangle className="w-3 h-3" /> will auto-clamp to {effective} at draft time
                            </span>
                        )}
                    </div>
                    {/* Fit error — no TPP works */}
                    {!fits && playerCount > 0 && teamCount > 0 && (
                        <div className="brutal-card-light p-3 border-heat text-xs space-y-1" data-testid="teams-per-player-fit-error">
                            <p className="flex items-center gap-2 text-heat font-bold">
                                <AlertTriangle className="w-3.5 h-3.5" /> Doesn't fit
                            </p>
                            <p className="text-zinc-400">
                                {playerCount} players need unique portfolios but only{" "}
                                <span className="text-white font-bold">{capacity.portfolios_available}</span>{" "}
                                exist at TPP={effective}. Add teams or remove players.
                            </p>
                        </div>
                    )}
                    {/* Friendly fit summary */}
                    {fits && (
                        <p className="text-[10px] text-zinc-500" data-testid="teams-per-player-fit-detail">
                            {mode === "overlap"
                                ? `Each crewmate gets a unique combination of ${effective} teams (drawing from ${capacity.portfolios_available} possible portfolios).`
                                : `Each crewmate gets ${effective} unique team${effective === 1 ? "" : "s"} — no overlap.`}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
